import { cva, VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';

import { cn } from './cn';

const alertVariants = cva('relative w-full rounded-auditor border text-sm', {
  variants: {
    tone: {
      neutral: 'border-line bg-surface text-ink',
      informative: 'border-informative/30 bg-informative-subtle text-ink',
      positive: 'border-positive/30 bg-positive-subtle text-ink',
      caution: 'border-caution/30 bg-caution-subtle text-ink',
      critical: 'border-critical/30 bg-critical-subtle text-ink',
      highlight: 'border-highlight-border bg-highlight text-ink',
    },
    /** Default matches most callouts; comfortable is for selectable source rows. */
    density: {
      default: 'p-4',
      comfortable: 'px-5 py-3.5',
    },
  },
  defaultVariants: {
    tone: 'neutral',
    density: 'default',
  },
});

export interface AlertProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  /** Controls shown at the trailing edge, such as a retry button. */
  action?: ReactNode;
  /** Shows a dismiss button. */
  onClose?: () => void;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ action, children, className, density, onClose, role = 'status', tone, ...props }, ref) => (
    <div
      ref={ref}
      role={role}
      className={cn(alertVariants({ tone, density }), (action || onClose) && 'flex items-start gap-3', className)}
      {...props}
    >
      {action || onClose ? (
        <>
          <div className="min-w-0 flex-1">{children}</div>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
          {onClose && (
            <button
              type="button"
              aria-label="Close"
              className="shrink-0 rounded-auditor p-0.5 text-ink-muted hover:bg-ink/10 hover:text-ink"
              onClick={onClose}
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          )}
        </>
      ) : (
        children
      )}
    </div>
  ),
);
Alert.displayName = 'Alert';

export const AlertTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn('mb-1 mt-0 text-sm font-semibold leading-none', className)} {...props} />
  ),
);
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('text-sm text-ink-muted', className)} {...props} />,
);
AlertDescription.displayName = 'AlertDescription';
